import {
  isProcessedAccount,
  type AccountType,
  type AppEvent,
} from "../api";

/** Mirror server capabilityTags fallback when payload.type is missing/unknown. */
function accountTypeFromTags(tags: string[]): AccountType {
  if (tags.includes("Send Money") || tags.includes("Bank Manager")) return "admin";
  if (tags.includes("Card Management") || tags.includes("Bill Pay")) return "medium";
  if (tags.includes("Employee: Card/Spend") || tags.includes("Employee: Blank")) {
    return "employee";
  }
  return "unknown";
}

function roleNamesFromPayload(payload: {
  details?: unknown[];
}): string[] | undefined {
  if (!Array.isArray(payload.details)) return undefined;
  for (const d of payload.details) {
    if (!d || typeof d !== "object") continue;
    const names = (d as { roleNames?: unknown }).roleNames;
    if (Array.isArray(names) && names.every((n) => typeof n === "string")) {
      return names as string[];
    }
  }
  return undefined;
}

function roleHasAdminWord(roleName: string): boolean {
  const r = String(roleName || "").trim().toUpperCase();
  return Boolean(r) && /(^|_)ADMIN(_|$)/.test(r);
}

/**
 * Source of truth: enrichment `payload.type`, with tag/role fallback matching server.
 */
export function classifyAccountType(event: AppEvent): AccountType {
  if (isProcessedAccount(event.payload)) {
    const t = event.payload.type;
    if (t === "admin" || t === "medium" || t === "employee") return t;

    const roles = roleNamesFromPayload(event.payload) || [];
    if (roles.some(roleHasAdminWord)) return "admin";

    const fromTags = accountTypeFromTags(event.payload.tags || []);
    if (fromTags === "admin") return "admin";

    if (roles.some((r) => {
      const u = r.toUpperCase();
      return u !== "EMPLOYEE" && u !== "CORPORATE_CARD_USER";
    })) {
      return fromTags === "medium" || fromTags === "unknown" ? "medium" : fromTags;
    }

    if (fromTags !== "unknown") return fromTags;
    if (roles.length > 0 && roles.every((r) => {
      const u = r.toUpperCase();
      return u === "EMPLOYEE" || u === "CORPORATE_CARD_USER";
    })) {
      return "employee";
    }
    return "unknown";
  }
  return "unknown";
}

/** Employee sidebar — no Station stock actions. */
export function isEmployeeAccount(event: AppEvent): boolean {
  return classifyAccountType(event) === "employee";
}

/** Medium sidebar — elevated (Bill Pay / Card Mgmt / …), no Station stock. */
export function isMediumAccount(event: AppEvent): boolean {
  return classifyAccountType(event) === "medium";
}

/** Listed only (Employee or Medium) — Station must not auto-stock. */
export function isListedOnlyAccount(event: AppEvent): boolean {
  const t = classifyAccountType(event);
  return t === "employee" || t === "medium";
}

/** Station → Available Stock (50% passkey / 50% admin_user). */
export function isAdminAccount(event: AppEvent): boolean {
  return classifyAccountType(event) === "admin";
}

export function accountTypeLabel(event: AppEvent): string {
  const c = classifyAccountType(event);
  if (c === "admin") return "Admin";
  if (c === "medium") return "Medium";
  if (c === "employee") return "Employee";
  return "Unknown";
}
